import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { QueryKeys } from 'src/constants/QueryKeys'
import {
    AdminAffiliateDetail,
    AdminAffiliateRow,
    AdminCommission,
    AdminPayout,
    AffiliateStatus,
    Invite,
    Overview,
    Paginated,
    PayoutStatus,
    Program,
    RateHistoryEntry,
    Referral,
    ReviewQueueItem,
} from 'src/types/Affiliate'
import { apiGet, apiPatch, apiPost } from 'src/utils/affiliateApi'
import { retryServerErrors } from 'src/utils/apiReason'

// Admin endpoints (`manageAffiliates` right). Every query lives under one key prefix so a mutation can
// simply refresh the whole admin area.

const key = (...parts: unknown[]) => [QueryKeys.adminAffiliates, ...parts]

// `paged` keeps the previous page on screen while the next loads; per-affiliate queries must not, or
// affiliate A's data would show while B loads.
const useAdminQuery = <T>(parts: unknown[], fn: () => Promise<T>, { paged = false, enabled = true } = {}) =>
    useQuery({
        queryKey: key(...parts),
        queryFn: fn,
        enabled,
        retry: retryServerErrors,
        placeholderData: paged ? keepPreviousData : undefined,
    })

const useAdminMutation = <TVars, TData>(fn: (vars: TVars) => Promise<TData>) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: fn,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: [QueryKeys.adminAffiliates] }),
    })
}

/* --------------------------------- Queries --------------------------------- */

export const useAdminOverview = () => useAdminQuery(['overview'], () => apiGet<Overview>('/overview'))

export const useAdminAffiliateList = (params: { status?: AffiliateStatus | ''; q?: string; page: number }) =>
    useAdminQuery(['list', params], () =>
        apiGet<Paginated<AdminAffiliateRow>>('', {
            status: params.status || undefined,
            q: params.q || undefined,
            page: params.page,
            limit: 20,
        }),
        { paged: true }
    )

export const useAdminAffiliate = (affiliateId: string, enabled = true) =>
    useAdminQuery(['detail', affiliateId], () => apiGet<AdminAffiliateDetail>(`/${affiliateId}`), { enabled })

export const useAdminAffiliateReferrals = (affiliateId: string, page: number) =>
    useAdminQuery(['referrals', affiliateId, page], () =>
        apiGet<Paginated<Referral>>(`/${affiliateId}/referrals`, { page, limit: 20 })
    )

export const useAdminAffiliateCommissions = (affiliateId: string, page: number) =>
    useAdminQuery(['commissions', affiliateId, page], () =>
        apiGet<Paginated<AdminCommission>>(`/${affiliateId}/commissions`, { page, limit: 20 })
    )

export const useAdminAffiliatePayouts = (affiliateId: string, page: number) =>
    useAdminQuery(['payouts', affiliateId, page], () =>
        apiGet<Paginated<AdminPayout>>(`/${affiliateId}/payouts`, { page, limit: 20 })
    )

export const useAdminRateHistory = (affiliateId: string) =>
    useAdminQuery(['rate-history', affiliateId], () => apiGet<RateHistoryEntry[]>(`/${affiliateId}/rate-history`))

export const useReviewQueue = (page: number) =>
    useAdminQuery(['review-queue', page], () =>
        apiGet<Paginated<ReviewQueueItem>>('/review-queue', { page, limit: 20 }), { paged: true }
    )

export const useInvites = (page: number) =>
    useAdminQuery(['invites', page], () => apiGet<Paginated<Invite>>('/invites', { page, limit: 20 }), { paged: true })

export const useAdminProgram = () => useAdminQuery(['program'], () => apiGet<Program>('/program'))

export const useAllPayouts = (status: PayoutStatus | '', page: number) =>
    useAdminQuery(['all-payouts', status, page], () =>
        apiGet<Paginated<AdminPayout>>('/payouts/all', { status: status || undefined, page, limit: 20 }), {
            paged: true,
        }
    )

/* -------------------------------- Mutations -------------------------------- */

export type CreateAffiliateBody = {
    userId?: string
    discordId?: string
    code?: string
    firstMonthBps?: number
    recurringBps?: number
    minPayoutCents?: number
    notes?: string
}

export const useCreateAffiliate = () =>
    useAdminMutation((body: CreateAffiliateBody) => apiPost<AdminAffiliateDetail>('', body))

export type UpdateAffiliateBody = {
    status?: AffiliateStatus
    terminationReason?: 'fraud' | 'other'
    code?: string
    firstMonthBps?: number | null
    recurringBps?: number | null
    minPayoutCents?: number | null
    notes?: string
}

export const useUpdateAffiliate = (affiliateId: string) =>
    useAdminMutation((body: UpdateAffiliateBody) => apiPatch<AdminAffiliateDetail>(`/${affiliateId}`, body))

/** For list rows (applications queue), where the id varies per call. */
export const useUpdateAnyAffiliate = () =>
    useAdminMutation(({ id, body }: { id: string; body: UpdateAffiliateBody }) =>
        apiPatch<AdminAffiliateDetail>(`/${id}`, body)
    )

export const useCreateAdjustment = (affiliateId: string) =>
    useAdminMutation((body: { amountCents: number; note: string }) =>
        apiPost<AdminCommission>(`/${affiliateId}/adjustments`, body)
    )

export const useFinalPayout = (affiliateId: string) =>
    useAdminMutation(() => apiPost<AdminPayout>(`/${affiliateId}/final-payout`))

export const useReviewCommission = () =>
    useAdminMutation(({ id, decision, note }: { id: string; decision: 'clear' | 'reject'; note?: string }) =>
        apiPost<AdminCommission>(`/commissions/${id}/review`, { decision, note })
    )

export type CreateInviteBody = {
    email?: string
    firstMonthBps?: number
    recurringBps?: number
    minPayoutCents?: number
    expiresInDays?: number
}

export const useCreateInvite = () =>
    useAdminMutation((body: CreateInviteBody) => apiPost<{ id: string; url: string; expiresAt: string }>('/invites', body))

export const useUpdateProgram = () =>
    useAdminMutation((body: Partial<Omit<Program, 'history'>>) => apiPatch<Program>('/program', body))

export const useApprovePayout = () => useAdminMutation((id: string) => apiPost<AdminPayout>(`/payouts/${id}/approve`))

export const useRejectPayout = () =>
    useAdminMutation(({ id, reason }: { id: string; reason: string }) =>
        apiPost<AdminPayout>(`/payouts/${id}/reject`, { reason })
    )

export const useRecheckPayout = () => useAdminMutation((id: string) => apiPost<AdminPayout>(`/payouts/${id}/recheck`))
